'use client';

// Image attachments as a thumbnail strip that opens a keyboard-navigable
// lightbox (Esc / arrows). Mirrors the galleries in the Norway CRM and Dev CRM
// so change-request images read the same across all three apps.

import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { Button, IconButton } from './button';

function extractFilename(url: string, idx: number): string {
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop();
    if (last) return decodeURIComponent(last);
  } catch {
    // not a parseable URL — fall through to the generic name
  }
  return `attachment-${idx + 1}`;
}

export function AttachmentGallery({ urls }: { urls: string[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  const total = urls.length;
  const close = useCallback(() => setOpenIndex(null), []);
  const prev = useCallback(() => setOpenIndex((i) => (i === null ? null : (i - 1 + total) % total)), [total]);
  const next = useCallback(() => setOpenIndex((i) => (i === null ? null : (i + 1) % total)), [total]);

  useEffect(() => {
    if (openIndex === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, close, prev, next]);

  if (total === 0) return null;

  async function downloadAll() {
    setDownloading(true);
    try {
      for (let i = 0; i < urls.length; i++) {
        try {
          const response = await fetch(urls[i]);
          if (!response.ok) throw new Error(`status ${response.status}`);
          const blobUrl = URL.createObjectURL(await response.blob());
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = extractFilename(urls[i], i);
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(blobUrl);
          // Give the browser a beat between downloads so none get dropped.
          await new Promise((r) => setTimeout(r, 150));
        } catch {
          window.open(urls[i], '_blank', 'noopener,noreferrer');
        }
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {urls.map((url, i) => (
          <button
            type="button"
            key={`${url}-${i}`}
            onClick={() => setOpenIndex(i)}
            className="corner-brand-sm hover:border-accent-bright block overflow-hidden border border-zinc-800 transition-colors"
          >
            <img src={url} alt={extractFilename(url, i)} className="max-h-[150px] max-w-[200px] object-cover" loading="lazy" />
          </button>
        ))}
        {total >= 2 ? (
          <Button size="sm" icon={Download} loading={downloading} onClick={() => void downloadAll()}>
            Download all ({total})
          </Button>
        ) : null}
      </div>

      {openIndex !== null ? (
        <div
          className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 p-4"
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute top-4 right-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <IconButton
              icon={Download}
              label="Download"
              variant="secondary"
              onClick={() => window.open(urls[openIndex], '_blank', 'noopener,noreferrer')}
            />
            <IconButton icon={X} label="Close" variant="secondary" onClick={close} />
          </div>

          {total > 1 ? (
            <>
              <IconButton
                icon={ChevronLeft}
                label="Previous"
                variant="secondary"
                className="absolute top-1/2 left-4 -translate-y-1/2"
                onClick={(e) => { e.stopPropagation(); prev(); }}
              />
              <IconButton
                icon={ChevronRight}
                label="Next"
                variant="secondary"
                className="absolute top-1/2 right-4 -translate-y-1/2"
                onClick={(e) => { e.stopPropagation(); next(); }}
              />
            </>
          ) : null}

          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={urls[openIndex]}
              alt={extractFilename(urls[openIndex], openIndex)}
              className="corner-brand max-h-[85vh] max-w-[90vw] object-contain"
            />
            {total > 1 ? (
              <div className="corner-brand-xs absolute bottom-2 left-1/2 -translate-x-1/2 bg-zinc-900/80 px-2 py-0.5 text-xs text-zinc-300 tabular-nums">
                {openIndex + 1} / {total}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
